import React, { useState } from 'react';
import { BookOpen, Loader2, Plus, Search } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  searchLiterature,
  formatCitationLine,
  type LiteratureHit,
} from '@/lib/writer/literatureSearch';

interface Props {
  onInsert: (text: string) => void;
  disabled?: boolean;
}

/**
 * Admin-only quick literature search inside the Writer composer. Calls the
 * `writer-search-literature` edge function (OpenAlex, EU-proxied, no PII).
 * Chosen hits are inserted into the chat draft as a plain citation line so
 * the model can reference them explicitly.
 */
const WriterFindSourcesPopover: React.FC<Props> = ({ onInsert, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LiteratureHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async () => {
    const q = query.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    try {
      const hits = await searchLiterature({ query: q, limit: 8 });
      setResults(hits);
      if (hits.length === 0) setError('No matches found.');
    } catch (err) {
      setResults([]);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const insertHit = (hit: LiteratureHit) => {
    const line = formatCitationLine(hit);
    onInsert(line);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Find sources"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
            'text-muted-foreground hover:text-foreground hover:bg-muted/60 transition',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          <BookOpen className="h-3.5 w-3.5" />
          Find sources
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-[420px] p-0"
      >
        <form
          className="flex items-center gap-2 border-b border-border/60 px-3 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runSearch();
          }}
        >
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search OpenAlex (title, author, topic)…"
            aria-label="Literature search query"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
          />
          {loading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </form>
        <div className="max-h-[320px] overflow-y-auto">
          {error && (
            <p className="px-3 py-3 text-xs text-muted-foreground">{error}</p>
          )}
          {results.map((hit) => (
            <button
              key={hit.id || hit.title}
              type="button"
              onClick={() => insertHit(hit)}
              className="w-full text-left px-3 py-2 border-b border-border/40 last:border-b-0 hover:bg-muted/50 transition"
            >
              <p className="text-sm font-medium text-foreground line-clamp-2">
                {hit.title || 'Untitled'}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {hit.authors.slice(0, 3).join(', ')}
                {hit.authors.length > 3 ? ' et al.' : ''}
                {hit.year ? ` · ${hit.year}` : ''}
                {hit.venue ? ` · ${hit.venue}` : ''}
              </p>
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary/80">
                <Plus className="h-3 w-3" /> Insert citation
              </span>
            </button>
          ))}
          {!error && !loading && results.length === 0 && (
            <p className="px-3 py-4 text-xs text-muted-foreground">
              Search scholarly literature via OpenAlex. Results are proxied
              through the EU; nothing about your draft is shared.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WriterFindSourcesPopover;
